/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import Catalog16 from '@carbon/icons-react/es/catalog/16';
import Edit16 from '@carbon/icons-react/es/edit/16';
import Group16 from '@carbon/icons-react/es/group/16';
import ShareKnowledge16 from '@carbon/icons-react/es/share-knowledge/16';
import UserAvatar16 from '@carbon/icons-react/es/user--avatar/16';
import { Tooltip, Typography } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { PermissionSetType } from 'api/models';
import { View } from 'api/views/types';
import { Menu } from 'components/atoms/dropdown';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { NonDeletedView } from '../types';

const Name = styled(Typography.Paragraph).attrs({
  ellipsis: {
    rows: 1,
    tooltip: true,
  },
})`
  margin-bottom: 0;
  padding-right: var(--spacing-03);
`;

const Wrapper = styled.span`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  > span {
    display: flex;
  }
  ${Name} {
    width: calc(100% - var(--spacing-09));
  }
`;

const StyledMenuItem = styled(Menu.Item)`
  color: var(--gray-80);
  ant-menu-submenu-title {
    border: 0;
    height: var(--height-base);
    line-height: var(--height-base);
    margin-bottom: 0;
    margin-top: 0;
  }
`;

const MoreActionButton = styled.span`
  width: var(--spacing-07);
`;

const MoreActionsDropdownStyled = styled(MoreActionsDropdown)<{
  cursorType: boolean;
}>`
  width: var(--spacing-07);
  cursor: ${({ cursorType }) => (cursorType ? 'not-allowed' : 'pointer')};
`;

interface Props<ViewDefinition = any> {
  view: NonDeletedView<ViewDefinition>;
  currentUserId: number;
  onViewSelect: (view: View<ViewDefinition>) => void;
  onViewEdit: (view: View<ViewDefinition>) => void;
  onViewDelete: (view: View<ViewDefinition>) => void;
  currentUserPermissionSetType: PermissionSetType;
  isExternalUser: boolean;
  isProjectTaskList: string;
}
export const renderViewListItem: FC<Props> = ({
  view,
  currentUserId,
  onViewSelect,
  onViewEdit,
  onViewDelete,
  currentUserPermissionSetType,
  isExternalUser,
  isProjectTaskList,
}: Props): JSX.Element => {
  const [, currentView] = view;
  const sharedWithMe = currentView.createdBy !== currentUserId;
  const sharedByMe =
    currentView.createdBy === currentUserId && currentView.self === false;

  const isAccountAdmin =
    currentUserPermissionSetType === PermissionSetType.AccountAdmin;
  const isSuperUser =
    currentUserPermissionSetType === PermissionSetType.SuperUser;
  const isProjectOwner =
    currentUserPermissionSetType === PermissionSetType.ProjectOwner;
  const disableViewFromAllTasks = !!(
    view[1].viewFilterShareConfig?.shareWithProjects && isProjectTaskList
  );
  const sharedIcon = (
    <Tooltip title={<FormattedMessage id="views.view_shared_by_me.tooltip" />}>
      <Group16 />
    </Tooltip>
  );
  const onlyMeIcon = (
    <Tooltip title={<FormattedMessage id="views.view_only_for_me.tooltip" />}>
      <UserAvatar16 />
    </Tooltip>
  );

  const sharedWithProjectIcon = (
    <Tooltip
      title={<FormattedMessage id="views.view.shared_with_projects.tooltip" />}
    >
      <Catalog16 />
    </Tooltip>
  );

  const sharedWithCustomerIcon = (shareWithProjects: boolean) => (
    <Tooltip
      title={
        <FormattedMessage
          id={
            shareWithProjects
              ? 'views.view.shared_with_customer.tooltip'
              : 'views.view.shared_with_customer_in_project.tooltip'
          }
        />
      }
    >
      <ShareKnowledge16 />
    </Tooltip>
  );

  return (
    <StyledMenuItem
      icon={sharedWithMe ? null : sharedByMe ? sharedIcon : onlyMeIcon}
      style={{ color: 'var(--gray-80)' }}
      key={currentView.viewId}
      onClick={() => onViewSelect(currentView)}
      data-cy={`views.${currentView.viewId}`}
    >
      <Wrapper>
        <Name>{currentView.viewName}</Name>

        {(currentView.viewFilterShareConfig?.shareWithProjects ||
          currentView.viewFilterShareConfig?.sharedWithCustomerPortal) &&
        !isExternalUser ? (
          <span>
            {!currentView.viewFilterShareConfig?.sharedWithCustomerPortal
              ? sharedWithProjectIcon
              : sharedWithCustomerIcon(
                  currentView.viewFilterShareConfig.shareWithProjects ?? false,
                )}
          </span>
        ) : null}

        <MoreActionButton onClick={(e) => e.stopPropagation()}>
          {isAccountAdmin || isSuperUser || isProjectOwner || !sharedWithMe ? (
            <MoreActionsDropdownStyled
              className="more-actions"
              placement="bottomLeft"
              disabled={disableViewFromAllTasks}
              cursorType={disableViewFromAllTasks}
              tooltipText={
                disableViewFromAllTasks ? (
                  <FormattedMessage
                    id="views.edit.from.all.tasks"
                    values={{
                      b: (chunks) => <strong>{chunks}</strong>,
                    }}
                  />
                ) : (
                  <FormattedMessage id="more_actions" />
                )
              }
              menu={
                <Menu>
                  <Menu.Item
                    key="edit"
                    icon={<Edit16 />}
                    onClick={() => onViewEdit(currentView)}
                    data-cy={`views.edit.${currentView.viewId}`}
                  >
                    <FormattedMessage id="edit" />
                  </Menu.Item>
                  <Menu.ItemDelete
                    key="delete"
                    onClick={() => onViewDelete(currentView)}
                    data-cy={`views.delete.${currentView.viewId}`}
                  />
                </Menu>
              }
            />
          ) : null}
        </MoreActionButton>
      </Wrapper>
    </StyledMenuItem>
  );
};
